import type { AdminRealm } from '@/lib/adminAuth';

/**
 * WHITE-LABEL BRAND CONFIG — single source of truth.
 *
 * Every admin surface (login, hub, planner, AI tools, academy, certificate)
 * reads its visible branding from here so a realm can be fully re-skinned
 * without touching feature code.
 *
 *  • albania / romania -> Zera Travel (dark, navy + gold, luxury travel)
 *  • lewith            -> Lewith & Freeman Real Estate (white-label real
 *                         estate CRM: white surfaces, deep blue + gold)
 *  • mtnshop           -> The MTN Shop (shopmtn.com — rigging & event
 *                         production gear; black & white portal skin)
 */

export interface GenerationBrand {
  /** Company the copywriter AI writes as. */
  company: string;
  /** One-line persona for the AI system prompt. */
  persona: string;
  /** Real places used for locations / fallback posts. */
  place_pool: string[];
  /** Descriptor appended after a location in image prompts. */
  place_suffix: string;
  /** Default CTA when the model omits one. */
  default_cta: string;
  /** Default hashtag pool for fallbacks. */
  hashtag_pool: string[];
}

export interface AdminBrand {
  realm: AdminRealm;
  /** Full legal-ish company name. */
  company: string;
  /** Short display name (header logo text). */
  shortName: string;
  /** Portal title, e.g. "Zera Admin". */
  portalName: string;
  /** Small eyebrow label on the login page. */
  loginEyebrow: string;
  /** Footer line on the login page. */
  loginFooter: string;
  /** Hub welcome heading pieces. */
  welcomeLead: string;
  welcomeAccent: string;
  welcomeSub: string;
  /** Industry noun used in copy ("agency" / "brokerage"). */
  industryNoun: string;
  /** Section cards on the hub. */
  crmTitle: string;
  crmDesc: string;
  socialTitle: string;
  socialDesc: string;
  adminTitle: string;
  adminDesc: string;
  /** Academy / certificate labels. */
  academyName: string;
  certIssuer: string;
  certFooter: string;
  /** Accent hexes (gold kept across brands; primary differs). */
  accent: string; // gold
  primary: string; // deep navy / brand blue
  /** Instagram identity used by the in-app post previews. */
  igHandle: string; // e.g. "shopwithmtn" — shown as the account name
  igLocation: string; // e.g. "Wilkes-Barre, PA" — the location line
  /** Profile picture shown in the post previews (falls back to a letter avatar). */
  igAvatar?: string;
  igUrlPlaceholder: string; // placeholder for the "paste a profile URL" input

  hashtagPlaceholder: string; // placeholder for hashtag inputs
  /** Brand context forwarded to the AI generation edge functions. */
  generation: GenerationBrand;
}

const ZERA_GENERATION: GenerationBrand = {
  company: 'Zera Travel',
  persona:
    'a senior social media copywriter for Zera Travel, a luxury Albanian travel concierge',
  place_pool: ['Ksamil', 'Theth', 'Berat', 'Albanian Riviera', 'Gjirokastër', 'Saranda', 'Vlorë'],
  place_suffix: 'Albania',
  default_cta: 'Plan your Albanian escape with Zera Travel.',
  hashtag_pool: ['#Albania', '#VisitAlbania', '#AlbanianRiviera', '#LuxuryTravel', '#HiddenGems', '#TravelAlbania'],
};

// Romania realm (/romaniaadmin) — same Zera Travel brand voice, but posts are
// about ROMANIA. Drives the weekly hotel story arc (Mon location → Tue hotel →
// Wed amenities → Thu local life → Fri culture → weekend continuation).
const ROMANIA_GENERATION: GenerationBrand = {
  company: 'Zera Travel',
  persona:
    'a senior social media copywriter for Zera Travel, a luxury Romanian travel concierge',
  place_pool: ['Brașov', 'Sibiu', 'Sighișoara', 'Bucharest', 'Cluj-Napoca', 'Sinaia', 'Maramureș'],
  place_suffix: 'Romania',
  default_cta: 'Plan your Romanian escape with Zera Travel.',
  hashtag_pool: ['#Romania', '#VisitRomania', '#Transylvania', '#LuxuryTravel', '#HiddenGems', '#TravelRomania'],
};


const ZERA_BASE: Omit<AdminBrand, 'realm'> = {
  company: 'Zera Travel',
  shortName: 'Zera',
  portalName: 'Zera Admin',
  loginEyebrow: 'Private Access',
  loginFooter: 'ZERA TRAVEL INTERNAL TOOLS',
  welcomeLead: 'Welcome to',
  welcomeAccent: 'Zera',
  welcomeSub:
    'Everything you need to run the agency and the brand — in one calm, premium workspace.',
  industryNoun: 'agency',
  crmTitle: 'Travel Agency CRM',
  crmDesc:
    'Customer forms, journeys, touchpoints, and the full booking workflow — organized, editable, movable.',
  socialTitle: 'Social Media Hub',
  socialDesc:
    'Five-step content engine: learn the brand voice, plan posts, generate media, approve, and publish.',
  adminTitle: 'Administration Suite',
  adminDesc:
    'Secure admin approval, temporary access codes, and operational controls for the team.',
  academyName: 'MTN Social Academy',
  certIssuer: 'Zera Travel · Admin Academy',
  certFooter: 'zeratravel.com · MTN Social Academy',
  accent: '#C9A961',
  primary: '#1A3B5C',
  igHandle: 'zeratravel',
  igLocation: 'Albanian Riviera',
  igUrlPlaceholder: 'https://instagram.com/zeratravel',
  hashtagPlaceholder: '#ZeraTravel #AlbanianRiviera',
  generation: ZERA_GENERATION,
};

export const LEWITH_BRAND: AdminBrand = {
  realm: 'lewith',
  company: 'Lewith & Freeman Real Estate',
  shortName: 'Lewith & Freeman',
  portalName: 'Lewith & Freeman Admin',
  loginEyebrow: 'Agent & Staff Access',
  loginFooter: 'LEWITH & FREEMAN REAL ESTATE · INTERNAL PLATFORM',
  welcomeLead: 'Welcome to',
  welcomeAccent: 'Lewith & Freeman',
  welcomeSub:
    'Everything you need to run the brokerage and the brand — listings, marketing, and approvals in one premium workspace.',
  industryNoun: 'brokerage',
  crmTitle: 'Real Estate CRM',
  crmDesc:
    'Buyers, sellers, leads, showings and the full transaction workflow — organized, editable, movable.',
  socialTitle: 'Marketing Hub',
  socialDesc:
    'Five-step listing marketing engine: learn the brand voice, plan posts, generate property media, approve, and publish.',
  adminTitle: 'Broker Administration',
  adminDesc:
    'Secure listing approvals, temporary access codes, and operational controls for the office.',
  academyName: 'Marketing Hub Academy',
  certIssuer: 'Lewith & Freeman · Agent Academy',
  certFooter: 'lewith-freeman.com · Marketing Hub Academy',
  accent: '#C9A961',
  primary: '#1E4E8C',
  igHandle: 'lewithfreeman',
  igLocation: 'Northeastern Pennsylvania',
  igUrlPlaceholder: 'https://instagram.com/lewithfreeman',
  hashtagPlaceholder: '#LewithFreeman #NEPARealEstate',
  generation: {
    company: 'Lewith & Freeman Real Estate',
    persona:
      'a senior social media copywriter for Lewith & Freeman Real Estate, a premier residential and commercial brokerage in Northeastern Pennsylvania',
    place_pool: ['Kingston', 'Clarks Summit', 'Dallas', 'Shavertown', 'Harveys Lake', 'Mountain Top', 'Wilkes-Barre'],
    place_suffix: 'Northeastern Pennsylvania',
    default_cta: 'Find your place with Lewith & Freeman Real Estate.',
    hashtag_pool: ['#LewithFreeman', '#NEPARealEstate', '#JustListed', '#DreamHome', '#LuxuryListings', '#PennsylvaniaHomes'],
  },
};

/**
 * The MTN Shop (realm: mtnshop) — powers the Social Media Hub inside the
 * standalone /SocialPopAdmin portal.
 *
 * GENERATION CONTEXT = LEARNED DATA. This block was built by scanning
 * shopmtn.com and the @shopwithmtn Instagram: the brand speaks as the rigging
 * expert behind the counter — direct, spec-literate, product-first and
 * safety-first. It celebrates the brands it supplies (CM, Lift-All, Petzl,
 * KASK, Kuzar, Tyler Truss, German Bionic, Konligo) and its MTN house line
 * (MTN Round Slings, MTN Half Coupler, MTN Chain Slider, MTN BOX, MTN Clamps).
 * Promotions are stated plainly — "Free Shipping on Petzl Height Safety Gear",
 * "New Brand Added: Tyler Truss Systems" — never hyped. Visuals: clean product
 * shots on white or dark industrial sets in the brand's matte black / signal
 * red / steel grey palette.
 */
export const MTNSHOP_BRAND: AdminBrand = {
  realm: 'mtnshop',
  company: 'The MTN Shop',
  shortName: 'The MTN Shop',
  portalName: 'MTN Shop Admin',
  loginEyebrow: 'Crew Access',
  loginFooter: 'THE MTN SHOP · INTERNAL PLATFORM',
  welcomeLead: 'Welcome to',
  welcomeAccent: 'The MTN Shop',
  welcomeSub:
    'Everything you need to run the shop and the brand — products, marketing, and approvals in one workspace.',
  industryNoun: 'shop',
  crmTitle: 'Shop CRM',
  crmDesc: 'Customers, quotes, orders and the full sales workflow — organized, editable, movable.',
  socialTitle: 'Social Media Hub',
  socialDesc:
    'Six-step content engine: learn the brand voice, generate a week, edit posts, approve, export, and connect analytics.',
  adminTitle: 'Administration Suite',
  adminDesc: 'Secure admin approval, temporary access codes, and operational controls for the team.',
  academyName: 'MTN Social Academy',
  certIssuer: 'The MTN Shop · Admin Academy',
  certFooter: 'shopmtn.com · MTN Social Academy',
  accent: '#C9A961',
  primary: '#0D1B2A',
  igHandle: 'shopwithmtn',
  igLocation: 'Wilkes-Barre, PA',
  // @shopwithmtn profile picture — the official MTN SHOP™ logo (stencil
  // wordmark on matte black with the multicolor mountain ridge). Shown in the
  // Instagram post previews so posts read as instagram.com/shopwithmtn.
  igAvatar: 'https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1783426723545_b965b815.png',
  igUrlPlaceholder: 'https://www.instagram.com/shopwithmtn/',

  hashtagPlaceholder: '#MTNShop #Rigging #EventProduction',
  generation: {
    company: 'The MTN Shop',
    persona:
      'the social media voice of The MTN Shop (shopmtn.com, Instagram @shopwithmtn) — the rigging expert for industrial and entertainment. Writes like the trusted pro behind the counter: direct, spec-literate (WLL, rated capacity, OSHA-certified), safety-first and zero-fluff. Proudly features the brands it supplies — CM Lodestar, Lift-All, Petzl, KASK, Kuzar, Tyler Truss Systems, German Bionic, Konligo — and its MTN house line (MTN Round Slings, MTN Half Coupler, MTN Chain Slider, MTN BOX). States promos plainly ("Free Shipping on Petzl Height Safety Gear", "New Brand Added") and closes with quote- and link-in-bio-driven CTAs',
    place_pool: [
      'the MTN Shop warehouse in Wilkes-Barre',
      'an arena rigging grid during load-in',
      'a festival main stage build at dawn',
      'a theater fly loft',
      'a touring production shop floor',
      'an industrial plant lifting bay',
      'a rope-access job site at height',
    ],
    place_suffix: 'professional rigging and live event production',
    default_cta: 'Shop it now at shopmtn.com — or DM us for a quote.',
    hashtag_pool: [
      '#MTNShop',
      '#Rigging',
      '#RiggingLife',
      '#EventProduction',
      '#StageCrew',
      '#LiveEvents',
      '#ChainHoist',
      '#Truss',
      '#HeightSafety',
      '#FallProtection',
      '#Backstage',
      '#RigItRight',
    ],
  },
};

export const ADMIN_BRANDS: Record<AdminRealm, AdminBrand> = {
  albania: { ...ZERA_BASE, realm: 'albania' },
  // Romania keeps the Zera skin but generates ROMANIAN content (its own
  // place pool, hashtags and persona) for the weekly hotel story arc.
  romania: {
    ...ZERA_BASE,
    realm: 'romania',
    igLocation: 'Transylvania, Romania',
    hashtagPlaceholder: '#ZeraTravel #VisitRomania',
    generation: ROMANIA_GENERATION,
  },
  lewith: LEWITH_BRAND,
  mtnshop: MTNSHOP_BRAND,
};


export function brandForRealm(realm: AdminRealm): AdminBrand {
  return ADMIN_BRANDS[realm] || ADMIN_BRANDS.albania;
}

/** Brand of the ACTIVE realm — realm-aware UI (post previews etc.) uses this. */
export function activeBrand(): AdminBrand {
  // Lazy import avoided: getAdminRealm lives in adminAuth which this file
  // already types against; import here would be circular-safe but we keep the
  // helper in adminAuth's consumers. Simplest: read the realm directly.
  try {
    const v = sessionStorage.getItem('mtnadmin_realm');
    if (v === 'romania' || v === 'lewith' || v === 'mtnshop') return ADMIN_BRANDS[v];
    return ADMIN_BRANDS.albania;
  } catch {
    return ADMIN_BRANDS.albania;
  }
}

/** True when the realm renders the light, real-estate white-label skin. */
export function isWhiteLabel(realm: AdminRealm): boolean {
  return realm === 'lewith';
}
